import React from 'react';
import { APITypes } from '../../../../../../../primary-record-types';
import { useProfilesTabNavigation } from '../../../../../../../utils/appNavigationNames';
import { useRichTableState } from '../../../../../../v2BaseComponents/RichTable/useRichTableState';
import useOrganizationClientRecordsTableSettings from '../../../../../../../hooks/api/useOrganizationClientRecordsTableSettings';
import { getIntl } from '../../../../../../../utils/intl';
import getName from '../../../../../../../utils/getName';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';
import { clientRecordStatus } from '../../../../../../../utils/sharedMessages';
import { TableActionOrGroup, TableConfig } from '../../../../../../v2BaseComponents/RichTable/types';
import RichTable from '../../../../../../v2BaseComponents/RichTable';
import { useMaybeRenderedSideDrawerToggle } from '../../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import EditClientGroupsSideDrawerContent from './EditClientGroupsSideDrawerContent';
import { useIsOrganizationAdmin, useIsOrganizationBillingAdmin } from '../../../../../../../hooks/api/useOrganization';
import AddClientRecordSideDrawerContent from './AddClientRecordSideDrawerContent';
import AddClientInvitationSideDrawerContent from './AddClientInvitationSideDrawerContent';
import { usePromptButton } from '../../../../../../shared/PromptButton';
import ReEngageClientPromptContent from './ReEngageClientPromptContent';
import EditClientRecordSideDrawerContent from './EditClientRecordSideDrawerContent';
import AddClientRecordInvitationSideDrawerContent from './AddClientRecordInvitationSideDrawerContent';
import RevokeInvitationPromptContent from './RevokeInvitationPromptContent';
import useOrganizationClientInvitations from '../../../../../../../hooks/api/useOrganizationClientInvitations';
import {
    WithClientGroupDetails,
    useOrganizationClientRecords,
} from '../../../../../../../hooks/api/useOrganizationClientRecords';
import ArchiveClientPromptContent from './ArchiveClientPromptContent';
import useOrganizationMedicalProfiles from '../../../../../../../hooks/api/useOrganizationMedicalProfiles';
import DeactivateClientPromptContent from './DeactivateClientPromptContent';
import UnArchiveClientPromptContent from './UnArchiveClientPromptContent';
import CopyOrganizationNotesSideDrawerContent from './CopyOrganizationNotesSideDrawerContent';
import { isPresent } from 'ts-is-present';
import CopyOrganizationCareTeamMemberSideDrawerContent from './CopyOrganizationCareTeamMemberSideDrawerContent';
import SendClientsMessageSideDrawerContent from './SendClientsMessageSideDrawerContent';
import ClientsActivitySideDrawerContent from './ClientsActivitySideDrawerContent';
import usePersistFileToClientProfiles from '../../../../../../../hooks/api/usePersistFileToClientProfiles';
import { useTempFilesPicker } from '../../../../../../v2BaseComponents/ImagePicker/TempFilePicker';
import Chip, { ChipSet, ChipSetChip } from '../../../../../../v2BaseComponents/Chip';
import useOrganizationAllowedMedicalProfileUsage from '../../../../../../../hooks/api/useOrganizationAllowedMedicalProfileUsage';
import Link from '../../../../../../v2BaseComponents/Link';
import CopyVisitToClientsSideDrawerContent from './CopyVisitToClientsSideDrawerContent';
import DeleteClientRecordsPromptContent from './DeleteClientRecordsPromptContent';
import { useIsMobileLayout } from '../../../../../../../hooks/useIsMobileLayout';
import EditClientInvitationSideDrawerContent from './EditClientInvitationSideDrawerContent';
import { useSelectedCategories } from './useSelectedCategories';
import { useRenderMultipleStatusSelectionDisplay } from './useRenderMultipleStatusSelectionDisplay';
import { useMultipleSelectionWarningPrompt } from './useMultipleSelectionWarningPrompt';
import { useFilterShownTableActions } from '../../../../../../v2BaseComponents/RichTable/TableActions/useFilterShownTableActions';
import BulkAddClientProfilesGroupsSideDrawerContent from './BulkAddClientProfilesGroupsSideDrawerContent';
import useOrganizationPaymentIntegration from '../../../../../../../hooks/api/useOrganizationPaymentIntegration';
import EditClientRecordBillingInformationSideDrawerContent from './EditClientRecordBillingInformationSideDrawerContent';
import useFeatureToggles from '../../../../../../../hooks/api/useFeatureToggles';
import ClientInvoiceItemsSideDrawerContent from './ClientInvoiceItemsSideDrawerContent';
import { useScreenParamWriter } from '../../../../../../../hooks/useScreenParamStorage';
import ArchiveClientsPromptContent from './ArchiveClientsPromptContent';
import RevokeInvitationsPromptContent from './RevokeInvitationsPromptContent';
import { useOrganizationClientProfileUnreadInAppNotificationCount } from '../../../../../../../hooks/api/useOrganizationClientProfileUnreadInAppNotificationCount';
import Stack from '../../../../../../v2BaseComponents/Stack';
import NotificationCountIndicator from '../../../../../../v2BaseComponents/NotificationIndicator/NotificationCountIndicator';
import MessagePreferencesSideDrawerContent from './MessagePreferencesSideDrawerContent';

const intl = getIntl('profiles-client-records-table', {
    details: 'Client Record',
    createDateTime: 'Created',
    status: 'Status',
    pointOfContactName: 'Point of Contact',
    pointOfContactEmail: 'Email',
    clientName: 'Client',
    clientDoB: 'Client DoB',
    editGroups: 'Groups',
    clientRecordDetails: 'Client Record Details',
    view: 'View',
    addClient: 'Add New Client',
    inviteNewClient: 'Invite New Client',
    inviteClient: 'Invite Client',
    reEngageClient: 'Re-Engage Client',
    editClientRecord: 'Client Record',
    revokeInvitation: 'Revoke Invitation',
    revokeInvitations: 'Revoke Invitations',
    archiveClient: 'Archive Client',
    archiveClients: 'Archive Clients',
    deactivateClient: 'Deactivate Client',
    unarchiveClient: 'Unarchive Client',
    clientRecordNote: 'Notes',
    shareNote: 'Note',
    shareNotes: 'Share Notes',
    shareCareTeamMember: 'Care Team Contact',
    shareCareTeamMembers: 'Share Care Team Members',
    history: 'History',
    sendClientsMessage: 'Message',
    activity: 'Activity',
    sendFiles: 'Files',
    sendVisit: 'Visit',
    groups: 'Groups',
    activityHistory: 'Activity History',
    maxProfiles: '{current} / {max} free clients used',
    share: 'Share',
    edit: 'Edit',
    send: 'Send',
    deleteClients: 'Delete Client{plural}',
    bulkAddClients: 'Bulk Add Clients',
    addClients: 'Add Clients',
    resendInvitationEmail: 'Resend Invitation Email',
    editInvitation: 'Edit Invitation',
    addToGroups: 'Add to Groups',
    billingInformation: 'Billing Information',
    invoice: 'Invoice',
    clientInvoicing: 'Client Invoicing',
    bulkInviteClients: 'Bulk Invite Clients',
    messagePreferences: 'Message Preferences',
});

export type ClientRecordsTableOwnProps = {
    clientRecords: APITypes.organizationService.ListOrganizationClientRecordsResponse['items'];
    isFetchingClientRecords: boolean;
};

export type ClientRecordsTableProps = ClientRecordsTableOwnProps;

export type TableItem = WithClientGroupDetails<APITypes.organizationService.OrganizationClientRecordDetails>;

const getSortedClientGroups = (tableItem: TableItem) =>
    (tableItem.organizationGroups || []).sort((a, b) => a.metadata.name.localeCompare(b.metadata.name));

const ClientRecordsTable: React.FC<ClientRecordsTableProps> = ({ clientRecords, isFetchingClientRecords }) => {
    const { getUnreadInAppNotificationCountForClientMedicalProfile } =
        useOrganizationClientProfileUnreadInAppNotificationCount({});
    const hasInvoicingFeature = useFeatureToggles().featureToggles.organizationClientInvoicing;
    const isClientBillingEnabled = useOrganizationPaymentIntegration({}).isOrganizationInValidStripeState;
    const isBillingAdmin = useIsOrganizationBillingAdmin();
    const isInvoicingEnabledForTable = isClientBillingEnabled && !!hasInvoicingFeature && isBillingAdmin;
    const isMobile = useIsMobileLayout();
    const medicalProfileUsage = useOrganizationAllowedMedicalProfileUsage();
    const isOrganizationAdmin = useIsOrganizationAdmin();
    const profilesTabNavigator = useProfilesTabNavigation();
    const [tableState, setTableState] = useRichTableState();
    const { tableSettings, upsertTableSettings } = useOrganizationClientRecordsTableSettings();

    const serverTableSettingsRef = React.useRef<
        APITypes.organizationService.GetOrganizationClientRecordsTableSettingsResponse | undefined
    >(undefined);

    React.useEffect(() => {
        if (tableSettings !== undefined && serverTableSettingsRef.current === undefined) {
            serverTableSettingsRef.current = tableSettings;
            if (tableSettings) {
                setTableState({
                    searchValue: '',
                    selectedRowKeys: [],
                    columnFilters: tableSettings.columnFilters,
                    sortState: tableSettings.sortState,
                });
            }
        }
    }, [tableSettings, setTableState]);

    React.useEffect(() => {
        if (serverTableSettingsRef.current === undefined) return;
        const serverSettings = serverTableSettingsRef.current;
        if (
            serverSettings &&
            tableState.columnFilters === serverSettings.columnFilters &&
            tableState.sortState === serverSettings.sortState
        ) {
            return;
        }
        upsertTableSettings({ columnFilters: tableState.columnFilters, sortState: tableState.sortState });
    }, [tableState.columnFilters, tableState.sortState, upsertTableSettings]);

    const {
        archiveClientRecord,
        unarchiveClientRecord,
        reEngageClientRecord,
        deleteClientRecords,
        archiveClientRecords,
    } = useOrganizationClientRecords({});
    const { deleteClientInvitation, resendInvitationEmails, isResendingInvitationEmails, deleteClientInvitations } =
        useOrganizationClientInvitations({});
    const { deleteOrganizationMedicalProfile } = useOrganizationMedicalProfiles({});
    const { isPersisting, persistFilesToClientProfiles } = usePersistFileToClientProfiles();
    const getPointOfContactName = (record: TableItem) => getName(record.clientOrganizerDetails);
    const getClientName = (record: TableItem) => getName(record.clientDetails);
    const getStatus = (record: TableItem) => clientRecordStatus(record.status);
    const getGroupsDisplay = (record: TableItem) => {
        const chips: ChipSetChip[] = [];
        const [first, second, ...rest] = getSortedClientGroups(record);
        if (first) {
            chips.push({ title: first.metadata.name });
        }

        if (second) {
            chips.push({ title: second.metadata.name });
        }

        if (rest.length > 0) {
            chips.push({ title: `+${rest.length}` });
        }

        return <ChipSet chips={chips} />;
    };
    const getGroupSearchValue = (record: TableItem) =>
        getSortedClientGroups(record)
            .map((_) => _.metadata.name)
            .join(',');
    const getGroupDiscreetValue = (record: TableItem) => getSortedClientGroups(record).map((_) => _.metadata.name);

    const selectedRecords = tableState.selectedRowKeys
        .map((_) => clientRecords.find((record) => record.organizationClientRecordId === _))
        .filter(isPresent);

    const singleSelectedRecord =
        tableState.selectedRowKeys.length === 1
            ? clientRecords.find((_) => _.organizationClientRecordId === tableState.selectedRowKeys[0]) || null
            : null;
    const { selectedCategories } = useSelectedCategories({
        selectedClients: selectedRecords,
    });
    const { renderMultipleStatusSelectionDisplay } = useRenderMultipleStatusSelectionDisplay({
        selectedClients: selectedRecords,
    });
    const bulkInviteClientWriter = useScreenParamWriter({
        onPersisted: (key) => {
            profilesTabNavigator.navigate('bulk-invite-clients', {
                clientRecordsStorageKey: key,
            });
        },
    });

    const editClientProfileGroupsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'editClientRecordGroups',
        title: intl('editGroups'),
        renderContent: (onClose) =>
            singleSelectedRecord ? (
                <EditClientGroupsSideDrawerContent
                    onClose={onClose}
                    clientRecordId={singleSelectedRecord.organizationClientRecordId}
                />
            ) : null,
        mobileOnFocus: () =>
            singleSelectedRecord
                ? profilesTabNavigator.navigate('edit-client-profile-groups', {
                      clientRecordId: singleSelectedRecord.organizationClientRecordId,
                  })
                : null,
    });

    const addClientsToGroupsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addClientsToGroups',
        title: intl('addToGroups'),
        renderContent: (onClose) => (
            <BulkAddClientProfilesGroupsSideDrawerContent
                onClose={onClose}
                clientRecordIds={selectedRecords.map((record) => record.organizationClientRecordId).filter(isPresent)}
            />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('bulk-add-client-profiles-groups', {
                clientRecordIds: selectedRecords
                    .filter(clientRecordStatusTypeGuard.active)
                    .map((record) => record.organizationClientRecordId)
                    .filter(isPresent),
            }),
    });

    const addClientRecordSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addClientRecord',
        title: intl('addClient'),
        renderContent: (onClose) => <AddClientRecordSideDrawerContent onClose={onClose} />,
        mobileOnFocus: () => profilesTabNavigator.navigate('add-client-record'),
    });

    const addClientInviteSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addClientInvite',
        title: intl('inviteNewClient'),
        renderContent: (onClose) => <AddClientInvitationSideDrawerContent onClose={onClose} />,
        mobileOnFocus: () => profilesTabNavigator.navigate('add-client-invitation'),
    });

    const editClientInvitationSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'editClientInvitation',
        title: intl('editInvitation'),
        renderContent: (onClose) => (
            <EditClientInvitationSideDrawerContent onClose={onClose} clientRecordId={tableState.selectedRowKeys[0]!} />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('edit-client-invitation', {
                clientRecordId: tableState.selectedRowKeys[0]!,
            }),
    });

    const editClientRecordSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'editClientRecord',
        title: intl('edit'),
        renderContent: (onClose) => (
            <EditClientRecordSideDrawerContent onClose={onClose} clientRecordId={tableState.selectedRowKeys[0]!} />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('edit-client-record', {
                clientRecordId: tableState.selectedRowKeys[0]!,
            }),
    });

    const inviteClientRecordSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'inviteClientRecord',
        title: intl('inviteClient'),
        renderContent: (onClose) => (
            <AddClientRecordInvitationSideDrawerContent
                onClose={onClose}
                clientRecordId={tableState.selectedRowKeys[0]!}
            />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('add-client-record-invitation', {
                clientRecordId: tableState.selectedRowKeys[0]!,
            }),
    });

    const messagePreferencesSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'messagePreferences',
        title: intl('messagePreferences'),
        renderContent: (onClose) => (
            <MessagePreferencesSideDrawerContent
                onClose={onClose}
                selectedClientRecordIds={tableState.selectedRowKeys}
            />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('message-preferences', {
                selectedClientRecordIds: tableState.selectedRowKeys,
            }),
    });

    const reEngageClientPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <ReEngageClientPromptContent
                onClose={onClose}
                organizationClientRecordId={tableState.selectedRowKeys[0]}
                onReEngage={(organizationClientRecordId) => {
                    reEngageClientRecord(organizationClientRecordId);
                    onClose();
                }}
            />
        ),
        promptTitle: intl('reEngageClient'),
    });

    const revokeInvitationPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <RevokeInvitationPromptContent
                onClose={onClose}
                organizationClientRecordId={tableState.selectedRowKeys[0]}
                onRevoke={(organizationClientInvitationId) => {
                    deleteClientInvitation(organizationClientInvitationId);
                    onClose();
                }}
                onEditInvitation={() => {
                    onClose();
                    editClientInvitationSideDrawer.onOpen();
                }}
            />
        ),
        promptTitle: intl('revokeInvitation'),
    });

    const revokeInvitationsPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <RevokeInvitationsPromptContent
                onClose={onClose}
                organizationClientRecordIds={tableState.selectedRowKeys}
                onRevoke={(organizationClientInvitationIds) => {
                    deleteClientInvitations(organizationClientInvitationIds);
                    onClose();
                }}
            />
        ),
        promptTitle: intl('revokeInvitations'),
    });

    const archiveClientPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <ArchiveClientPromptContent
                onClose={onClose}
                organizationClientRecordId={tableState.selectedRowKeys[0]}
                onArchive={(clientRecordId) => {
                    archiveClientRecord(clientRecordId);
                    onClose();
                }}
            />
        ),
        promptTitle: intl('archiveClient'),
    });

    const archiveClientsPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <ArchiveClientsPromptContent
                onClose={onClose}
                organizationClientRecordIds={tableState.selectedRowKeys}
                onArchive={(clientRecordIds) => {
                    archiveClientRecords(clientRecordIds);
                    onClose();
                }}
            />
        ),
        promptTitle: intl('archiveClients'),
    });

    const unarchiveClientPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <UnArchiveClientPromptContent
                onClose={onClose}
                organizationClientRecordId={tableState.selectedRowKeys[0]}
                onUnarchive={(clientRecordId) => {
                    unarchiveClientRecord(clientRecordId);
                    onClose();
                }}
            />
        ),
        promptTitle: intl('archiveClient'),
    });

    const deactivateClientPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <DeactivateClientPromptContent
                onClose={onClose}
                organizationClientRecordId={tableState.selectedRowKeys[0]}
                onDeactivate={(medicalProfileId) => {
                    deleteOrganizationMedicalProfile(medicalProfileId);
                    onClose();
                }}
            />
        ),
        promptTitle: intl('deactivateClient'),
    });

    const deleteClientsPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <DeleteClientRecordsPromptContent
                onClose={onClose}
                organizationClientRecordIds={tableState.selectedRowKeys}
                onDelete={deleteClientRecords}
            />
        ),
        promptTitle: intl('deleteClients', {
            plural: tableState.selectedRowKeys.length > 1 ? 's' : '',
        }),
    });

    const selectedMedicalProfileIds = selectedRecords.map((_) => _.clientMedicalProfileId).filter(isPresent);
    const copyOrgNoteToClientsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'copyOrgNoteToClients',
        title: intl('shareNotes'),
        renderContent: (onClose) => (
            <CopyOrganizationNotesSideDrawerContent
                destinationMedicalProfileIds={selectedMedicalProfileIds}
                onClose={onClose}
            />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('copy-org-note-to-clients', {
                medicalProfileIds: selectedMedicalProfileIds,
            }),
    });

    const copyOrgCareTeamMemberSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'shareCareTeamMember',
        title: intl('shareCareTeamMembers'),
        renderContent: (onClose) => (
            <CopyOrganizationCareTeamMemberSideDrawerContent
                onClose={onClose}
                destinationMedicalProfileIds={selectedMedicalProfileIds}
            />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('copy-org-care-team-member-to-clients', {
                medicalProfileIds: selectedMedicalProfileIds,
            }),
    });

    const sendVisitSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'sendVisit',
        title: intl('sendVisit'),
        renderContent: (onClose) => (
            <CopyVisitToClientsSideDrawerContent onClose={onClose} clientProfileIds={selectedMedicalProfileIds} />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('copy-visit-to-clients', {
                medicalProfileIds: selectedMedicalProfileIds,
            }),
    });

    const sendClientsMessageSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'sendClientsMessage',
        title: intl('sendClientsMessage'),
        renderContent: (onClose) => (
            <SendClientsMessageSideDrawerContent onClose={onClose} clientProfileIds={selectedMedicalProfileIds} />
        ),
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('send-clients-message', {
                clientMedicalProfileIds: selectedMedicalProfileIds,
            }),
    });

    const clientsActivitySideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'clientsActivity',
        title: intl('activity'),
        noScroll: true,
        renderContent: () => <ClientsActivitySideDrawerContent clientMedicalProfileIds={selectedMedicalProfileIds} />,
        mobileOnFocus: () =>
            profilesTabNavigator.navigate('clients-activity', {
                clientMedicalProfileIds: selectedMedicalProfileIds,
            }),
    });

    const clientBillingInformationSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'clientBillingInformation',
        title: intl('billingInformation'),
        renderContent: (onClose) => (
            <EditClientRecordBillingInformationSideDrawerContent
                onClose={onClose}
                clientRecordId={tableState.selectedRowKeys[0]!}
            />
        ),
        mobileOnFocus: () => {
            profilesTabNavigator.navigate('edit-client-billing-info', {
                clientRecordId: tableState.selectedRowKeys[0]!,
            });
        },
    });

    const clientInvoiceLineItemsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'clientInvoiceLineItems',
        title: intl('clientInvoicing'),
        renderContent: (onClose) => (
            <ClientInvoiceItemsSideDrawerContent
                onClose={onClose}
                clientRecordId={tableState.selectedRowKeys[0]!}
                onEditBillingDetails={() => {
                    onClose(true);
                    clientBillingInformationSideDrawer.onOpen();
                }}
            />
        ),
        mobileOnFocus: () => {
            profilesTabNavigator.navigate('client-invoice-items', {
                clientRecordId: tableState.selectedRowKeys[0]!,
            });
        },
    });

    const addFileTempPicker = useTempFilesPicker({
        onPick: (files) => {
            persistFilesToClientProfiles({
                tempFiles: files.map((file) => ({
                    tempFileId: file.tempFileId,
                    fileName: file.originalName || file.name,
                    contentType: file.contentType,
                })),
                destinationMedicalProfileIds: selectedMedicalProfileIds,
            });
        },
    });

    const tableActions: TableActionOrGroup<TableItem>[] = [
        {
            title: intl('resendInvitationEmail'),
            icon: 'Send',
            onPress: () => {
                if (selectedRecords.length && selectedRecords.every((_) => clientRecordStatusTypeGuard.invited(_))) {
                    resendInvitationEmails(selectedRecords.map((_) => _.organizationClientRecordId));
                }
            },
            shouldShow: () =>
                !!selectedRecords.length && selectedRecords.every((_) => clientRecordStatusTypeGuard.invited(_)),
            shouldDisable: () => isResendingInvitationEmails,
            testID: 'resend-invitation-email',
        },
        {
            title: intl('inviteClient'),
            icon: 'Send',
            onPress: inviteClientRecordSideDrawer.onOpen,
            shouldShow: () =>
                isOrganizationAdmin &&
                !!singleSelectedRecord &&
                (clientRecordStatusTypeGuard.notInvited(singleSelectedRecord) ||
                    clientRecordStatusTypeGuard.invitationRejected(singleSelectedRecord)),
            testID: 'invite-client-record',
            shouldDisable: () => medicalProfileUsage.hasHitLimit,
        },
        {
            title: intl('bulkInviteClients'),
            icon: 'Send',
            onPress: () => {
                bulkInviteClientWriter.writeValue(tableState.selectedRowKeys);
            },
            shouldShow: () =>
                isOrganizationAdmin &&
                selectedRecords.length > 1 &&
                selectedRecords.every(clientRecordStatusTypeGuard.notInvited),
            testID: 'bulk-invite-client-record',
            shouldDisable: () => medicalProfileUsage.hasHitLimit,
        },
        {
            title: intl('share'),
            icon: 'Share2',
            testID: 'client-record-share',
            actions: [
                {
                    title: intl('shareCareTeamMember'),
                    icon: 'Share2',
                    onPress: copyOrgCareTeamMemberSideDrawer.onOpen,
                    shouldShow: () =>
                        tableState.selectedRowKeys.length > 0 &&
                        selectedRecords.every(clientRecordStatusTypeGuard.active),
                    testID: 'copy-org-care-team-member-to-clients',
                },
                {
                    title: intl('shareNote'),
                    icon: 'Share2',
                    onPress: copyOrgNoteToClientsSideDrawer.onOpen,
                    shouldShow: () =>
                        tableState.selectedRowKeys.length > 0 &&
                        selectedRecords.every(clientRecordStatusTypeGuard.active),
                    testID: 'copy-org-note-to-clients',
                },
                {
                    title: intl('sendVisit'),
                    icon: 'Share2',
                    onPress: sendVisitSideDrawer.onOpen,
                    shouldShow: () =>
                        tableState.selectedRowKeys.length > 0 &&
                        selectedRecords.every(clientRecordStatusTypeGuard.active),
                    testID: 'send-visit',
                },
            ],
        },
        {
            title: intl('edit'),
            icon: 'Edit2',
            testID: 'client-record-edit',
            actions: [
                {
                    title: intl('editClientRecord'),
                    icon: 'Edit',
                    onPress: editClientRecordSideDrawer.onOpen,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        !!singleSelectedRecord &&
                        (clientRecordStatusTypeGuard.notInvited(singleSelectedRecord) ||
                            clientRecordStatusTypeGuard.archived(singleSelectedRecord)),
                    testID: 'edit-client-record',
                },
                {
                    title: intl('editGroups'),
                    icon: 'Edit',
                    onPress: () => {
                        editClientProfileGroupsSideDrawer.onOpen();
                    },
                    shouldShow: () => isOrganizationAdmin && !!singleSelectedRecord,
                    testID: 'edit-client-groups',
                },
                {
                    title: intl('addToGroups'),
                    icon: 'Edit',
                    onPress: addClientsToGroupsSideDrawer.onOpen,
                    shouldShow: () => isOrganizationAdmin && selectedRecords.length > 1,
                    testID: 'add-clients-to-groups',
                },
                {
                    title: intl('archiveClient'),
                    icon: 'Archive',
                    onPress: archiveClientPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        !!singleSelectedRecord &&
                        clientRecordStatusTypeGuard.notInvited(singleSelectedRecord),
                    testID: 'archive-client-record',
                },
                {
                    title: intl('archiveClients'),
                    icon: 'Archive',
                    onPress: archiveClientsPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        selectedRecords.length > 1 &&
                        selectedRecords.every(clientRecordStatusTypeGuard.notInvited),
                    testID: 'archive-client-records',
                },
                {
                    title: intl('unarchiveClient'),
                    icon: 'UserPlus',
                    onPress: unarchiveClientPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        !!singleSelectedRecord &&
                        clientRecordStatusTypeGuard.archived(singleSelectedRecord),
                    testID: 'unarchive-client-record',
                    shouldDisable: () => medicalProfileUsage.hasHitLimit,
                },
                {
                    title: intl('deactivateClient'),
                    icon: 'UserX',
                    onPress: deactivateClientPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        !!singleSelectedRecord &&
                        clientRecordStatusTypeGuard.active(singleSelectedRecord),
                    testID: 'deactivate-client-record',
                },
                {
                    title: intl('revokeInvitation'),
                    icon: 'Trash2',
                    onPress: revokeInvitationPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        !!singleSelectedRecord &&
                        clientRecordStatusTypeGuard.invited(singleSelectedRecord),
                    testID: 'revoke-client-invitation',
                },
                {
                    title: intl('revokeInvitations'),
                    icon: 'Trash2',
                    onPress: revokeInvitationsPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        selectedRecords.length > 1 &&
                        selectedRecords.every(clientRecordStatusTypeGuard.invited),
                    testID: 'revoke-client-invitations',
                },
                {
                    title: intl('editInvitation'),
                    icon: 'Edit',
                    onPress: editClientInvitationSideDrawer.onOpen,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        !!singleSelectedRecord &&
                        clientRecordStatusTypeGuard.invited(singleSelectedRecord),
                    testID: 'edit-client-invitation',
                },
                {
                    title: intl('reEngageClient'),
                    icon: 'Send',
                    onPress: reEngageClientPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        !!singleSelectedRecord &&
                        clientRecordStatusTypeGuard.inactive(singleSelectedRecord),
                    testID: 're-engage-client',
                    shouldDisable: () => medicalProfileUsage.hasHitLimit,
                },
                {
                    title: intl('deleteClients', {
                        plural: tableState.selectedRowKeys.length > 1 ? 's' : '',
                    }),
                    icon: 'Trash2',
                    onPress: deleteClientsPrompt.onPress,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        tableState.selectedRowKeys.length > 0 &&
                        selectedRecords.every(
                            (_) => clientRecordStatusTypeGuard.archived(_) || clientRecordStatusTypeGuard.inactive(_),
                        ),
                    testID: 'delete-clients',
                },
                {
                    title: intl('messagePreferences'),
                    icon: 'MessageSquare',
                    onPress: messagePreferencesSideDrawer.onOpen,
                    shouldShow: () =>
                        isOrganizationAdmin &&
                        tableState.selectedRowKeys.length > 0 &&
                        selectedRecords.every((_) => clientRecordStatusTypeGuard.active(_)),
                    testID: 'client-message-preferences',
                },
                {
                    title: intl('billingInformation'),
                    icon: 'CreditCard',
                    onPress: clientBillingInformationSideDrawer.onOpen,
                    shouldShow: () => isInvoicingEnabledForTable && !!singleSelectedRecord,
                    testID: 'client-billing-information',
                },
            ],
        },

        {
            title: 'Send',
            icon: 'Send',
            testID: 'client-record-send',
            actions: [
                {
                    title: intl('sendFiles'),
                    icon: 'Share2',
                    onPress: addFileTempPicker.onPress,
                    shouldShow: () =>
                        tableState.selectedRowKeys.length > 0 &&
                        selectedRecords.every(clientRecordStatusTypeGuard.active),
                    shouldDisable: () => isPersisting || addFileTempPicker.isLoadingFile,
                    renderAfter: () => addFileTempPicker.E2EInput,
                    testID: 'send-files',
                },
                {
                    title: intl('sendClientsMessage'),
                    icon: 'Send',
                    onPress: sendClientsMessageSideDrawer.onOpen,
                    shouldShow: () =>
                        tableState.selectedRowKeys.length > 0 &&
                        selectedRecords.every(clientRecordStatusTypeGuard.active),
                    testID: 'send-clients-message',
                },
            ],
        },
        {
            title: intl('invoice'),
            icon: 'CreditCard',
            testID: 'client-record-invoice',
            onPress: clientInvoiceLineItemsSideDrawer.onOpen,
            shouldShow: () => isInvoicingEnabledForTable && !!singleSelectedRecord,
        },
        {
            title: intl('inviteNewClient'),
            icon: 'Send',
            onPress: () => {
                addClientInviteSideDrawer.onOpen();
            },
            shouldShow: (selected) => isOrganizationAdmin && selected.length === 0,
            testID: 'invite-new-client',
            shouldDisable: () => medicalProfileUsage.hasHitLimit,
        },
        {
            title: intl('addClients'),
            icon: 'UserPlus',
            testID: 'add-clients',
            actions: [
                {
                    title: intl('addClient'),
                    icon: 'UserPlus',
                    onPress: () => {
                        addClientRecordSideDrawer.onOpen();
                    },
                    shouldShow: (selected) => isOrganizationAdmin && selected.length === 0,
                    testID: 'add-client-record',
                },
                {
                    title: intl('bulkAddClients'),
                    icon: 'Users',
                    onPress: () => {
                        profilesTabNavigator.navigate('bulk-add-client-records', {});
                    },
                    shouldShow: (selected) => isOrganizationAdmin && selected.length === 0,
                    testID: 'bulk-add-client-records',
                },
            ],
        },
        {
            title: intl('details'),
            icon: 'Info',
            onPress: () => {
                if (singleSelectedRecord?.organizationClientRecordId) {
                    profilesTabNavigator.navigate('client-record', {
                        clientRecordId: singleSelectedRecord.organizationClientRecordId,
                    });
                }
            },
            shouldShow: () => !!singleSelectedRecord,
            testID: 'client-record-details',
        },
        {
            title: intl('activity'),
            icon: 'Activity',
            onPress: clientsActivitySideDrawer.onOpen,
            shouldShow: () =>
                tableState.selectedRowKeys.length > 0 && selectedRecords.every(clientRecordStatusTypeGuard.active),
            testID: 'clients-activity',
        },
        {
            title: intl('activityHistory'),
            icon: 'Activity',
            shouldShow: () => !!singleSelectedRecord && clientRecordStatusTypeGuard.inactive(singleSelectedRecord),
            onPress: () => {
                profilesTabNavigator.navigate('client-activity-history', {
                    clientRecordId: tableState.selectedRowKeys[0]!,
                });
            },
            testID: 'client-activity-history',
        },
    ];

    const areAllActionsDisabled =
        useFilterShownTableActions({
            actions: tableActions,
            data: selectedRecords,
        }).shownActions.length === 0;
    const multipleSelectionWarningPrompt = useMultipleSelectionWarningPrompt({
        selectedClients: selectedRecords,
        areAllActionsDisabled,
    });

    const clientProfilesTableConfig: TableConfig<TableItem> = {
        columns: [
            {
                label: intl('clientName'),
                columnKey: 'client',
                getSortValue: getClientName,
                getContentKey: getClientName,
                getSearchValue: getClientName,
                render: (item) => {
                    let clientMedicalProfileId: string | null = null;
                    if (clientRecordStatusTypeGuard.active(item)) {
                        clientMedicalProfileId = item.clientMedicalProfileId || null;
                    }

                    if (clientRecordStatusTypeGuard.invited(item) && item.provisionedMedicalProfileId) {
                        clientMedicalProfileId = item.provisionedMedicalProfileId;
                    }

                    if (clientMedicalProfileId) {
                        const notificationCount =
                            getUnreadInAppNotificationCountForClientMedicalProfile(clientMedicalProfileId);
                        const name = getName(item.clientDetails);
                        return (
                            <Stack horizontal space={4} align="center">
                                <Link
                                    onPress={() => {
                                        if (isMobile) {
                                            profilesTabNavigator.navigate('profile', {
                                                profileId: clientMedicalProfileId!,
                                            });
                                        } else {
                                            profilesTabNavigator.navigate('profile-activity', {
                                                profileId: clientMedicalProfileId!,
                                            });
                                        }
                                    }}
                                    linkColor="blue"
                                    variant="table-content"
                                    noUnderline
                                    testID="client-record-link"
                                >
                                    {name}
                                </Link>
                                {notificationCount ? <NotificationCountIndicator count={notificationCount} /> : null}
                            </Stack>
                        );
                    } else {
                        return getName(item.clientDetails);
                    }
                },
            },
            {
                label: intl('status'),
                columnKey: 'status',
                getSortValue: getStatus,
                getContentKey: getStatus,
                getSearchValue: getStatus,
                render: getStatus,
                getDiscreetValue: getStatus,
                hideOnMobile: true,
            },
            {
                label: intl('pointOfContactName'),
                columnKey: 'pointOfContactName',
                getSortValue: getPointOfContactName,
                getContentKey: getPointOfContactName,
                getSearchValue: getPointOfContactName,
                render: getPointOfContactName,
                hideOnMobile: true,
            },
            {
                label: intl('groups'),
                columnKey: 'groups',
                getDiscreetValue: getGroupDiscreetValue,
                getSearchValue: getGroupSearchValue,
                render: getGroupsDisplay,
                getContentKey: getGroupSearchValue,
                getSortValue: getGroupSearchValue,
                hideOnMobile: true,
            },
        ],
        getRowKey: (item) => item.organizationClientRecordId,
    };

    const renderMaxProfilesChip = () => {
        if (medicalProfileUsage.isLoading || medicalProfileUsage?.allowedProfileUsage?.type !== 'limited') {
            return null;
        }

        return (
            <Chip
                testID="max-profiles-chip"
                title={intl('maxProfiles', {
                    current: medicalProfileUsage.allowedProfileUsage.currentMedicalProfileCount,
                    max: medicalProfileUsage.allowedProfileUsage.maxAllowedMedicalProfileCount,
                })}
                highlight={medicalProfileUsage.hasHitLimit}
            />
        );
    };

    return (
        <>
            <RichTable
                config={clientProfilesTableConfig}
                state={tableState}
                onStateChange={setTableState}
                data={clientRecords}
                minWidth={'100%'}
                allowRowSelection
                style={{ height: '100%' }}
                isFetching={isFetchingClientRecords}
                headerRight={renderMaxProfilesChip()}
                selectedCategories={selectedCategories}
                renderNoActionsPlaceholder={renderMultipleStatusSelectionDisplay}
                actions={tableActions}
            />
            {editClientRecordSideDrawer.SideDrawer}
            {addClientRecordSideDrawer.SideDrawer}
            {addClientInviteSideDrawer.SideDrawer}
            {inviteClientRecordSideDrawer.SideDrawer}
            {editClientProfileGroupsSideDrawer.SideDrawer}
            {reEngageClientPrompt.Prompt}
            {revokeInvitationPrompt.Prompt}
            {archiveClientPrompt.Prompt}
            {deactivateClientPrompt.Prompt}
            {unarchiveClientPrompt.Prompt}
            {copyOrgNoteToClientsSideDrawer.SideDrawer}
            {copyOrgCareTeamMemberSideDrawer.SideDrawer}
            {sendClientsMessageSideDrawer.SideDrawer}
            {clientsActivitySideDrawer.SideDrawer}
            {sendVisitSideDrawer.SideDrawer}
            {deleteClientsPrompt.Prompt}
            {editClientInvitationSideDrawer.SideDrawer}
            {multipleSelectionWarningPrompt.Prompt}
            {addClientsToGroupsSideDrawer.SideDrawer}
            {clientBillingInformationSideDrawer.SideDrawer}
            {clientInvoiceLineItemsSideDrawer.SideDrawer}
            {messagePreferencesSideDrawer.SideDrawer}
            {archiveClientsPrompt.Prompt}
            {revokeInvitationsPrompt.Prompt}
        </>
    );
};

export default React.memo(ClientRecordsTable);
